/* import __COLOCATED_TEMPLATE__ from './project-completion.hbs'; */
import { equal } from '@ember/object/computed';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { PROJECT_COMPLETION__SUB_VIEWS } from 'sh-www/constants/routes/project-completion';
import { handleErrorResponse } from 'sh-www/utils/handles-errors';
import { EMBER_ROUTE_NAMES } from "../../constants/routes";
import { task, timeout } from 'ember-concurrency';

const FORM_INPUTS = [
  'base-amount',
  'base-description',
];

/**
 * This is the completion flow for a project for buyer and sellers
 */
export default Component.extend({
  /**
   * @inheritDoc
   */
  tagName: 'div',
  classNames: ['SH-c-project-completion'],
  classNameBindings: [
    'state.isSubmittingProjectInformationForm:opacity-50'
  ],

  /**
   * Register services
   */
  state: service('states/routes/project-completion'),
  stateManager: service('state-managers/routes/project-completion'),
  applicationStateManager: service('state-managers/application'),
  router: service(),

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  /** @param {Boolean} allowExpenseAbilities Whether this component shows add/update expense when available  */
  allowExpenseAbilities: false,

  /*********************************
   * OPTIONAL PARAMS
   **********************************/

  /*********************************
   * LIFECYCLE FUNCTIONS
   **********************************/

  _updateTestBillingRecord: task(function *(immediate = false) {
    if (!immediate) {
      yield timeout(750);
    }

    try {
      yield this.get('stateManager.updateTestBillingRecord').perform();
      this.set('errors', {});
    } catch (error) {
      handleErrorResponse(this, error, FORM_INPUTS);
    }
  }).restartable(),

  /**********************************
   * PRIVATE
   **********************************/
  /** @type {String} The string to display above the table. */
  _billingRecordText: computed(
    'state.isAdvancePayment',
    'state.isBuyerViewing',
    function () {
      if (this.get('state.isBuyerViewing')) {
        return 'You will be charged the below balance.';
      } else {
        if (this.get('state.isAdvancePayment')) {
          return 'You will receive the below payment.';
        } else {
          return 'I confirm I’ve completed all required work for this project and that the balance below is what I am owed.';
        }
      }
    }
  ),

  _isReloading: computed(
    '_isUpdatingTestBillingRecord',
    'stateManager.loadApplication.isRunning',
    function() {
      return this._isUpdatingTestBillingRecord
        || this.stateManager.loadApplication.isRunning;
    }
  ),

  /**
   * @type {Boolean}      True if we want to show this component.
   */
  _isShowPrivateFeedback: equal('state.subView', PROJECT_COMPLETION__SUB_VIEWS.PRIVATE_FEEDBACK),

  _isUpdatingTestBillingRecord: computed(
    'stateManager.agreeAdvancePayment.isRunning',
    'stateManager.updateTestBillingRecord.isRunning',
    'stateManager.submitAdvancePayment.isRunning',
    function () {
      return this.get('stateManager.agreeAdvancePayment.isRunning')
        || this.get('stateManager.updateTestBillingRecord.isRunning')
        || this.get('stateManager.submitAdvancePayment.isRunning');
    }
  ),

  _payoutTitle: computed(
    'state.isBuyerViewing',
    function () {
      if (this.get('state.isBuyerViewing')) {
        return 'Payment';
      }
      return 'Payout';
    }
  ),

  _showExpediteOption: computed(
    'state.testBillingRecord.amount',
    function() {
      return this.state.testBillingRecord?.amount > 0;
    }
  ),

  _showProjectInformation: computed(
    'state.isBuyerViewing',
    'state.subView',
    function() {
      return this.get('state.isBuyerViewing') && this.get('state.subView') === PROJECT_COMPLETION__SUB_VIEWS.NONE;
    }
  ),

  _showPayment: equal('state.subView', PROJECT_COMPLETION__SUB_VIEWS.NONE),

  _showLineItemSelector: computed(
    'state.isAdvancePayment',
    'state.isApprovingAdvance',
    'state.isEditingAdvance',
    function() {
      return this.get('state.isAdvancePayment')
        && (!this.get('state.isApprovingAdvance') || this.get('state.isEditingAdvance'));
    }
  ),

  handleRefresh: task(function *() {
    yield this.stateManager.loadApplication.perform(this.state.application.id);
    yield this._updateTestBillingRecord.perform();
  }).drop(),

  /** @inheritDoc */
  init() {
    this._super(...arguments);
    this.set('errors', {});
  },

  actions: {
    onExpeditePayoutClicked() {
      const testBillingRecord = this.get('state.testBillingRecord');

      // Cannot expedite if we don't yet have a test record
      if (!testBillingRecord) {
        return;
      }

      this.setProperties({
        'state.expedite': true,
        'state.testBillingRecord.expedite': true,
      });

      this._updateTestBillingRecord.perform(true);
    },

    onSubmit() {
      if (this.get('state.isAdvancePayment')) {
        // Advance workflow
        if (this.get('state.isApprovingAdvance') && !this.get('state.isEditingAdvance')) {
          // Accepting an existing advance
          this.get('stateManager.agreeAdvancePayment').perform()
            .then(() => {
              this.set('errors', {});
              this.get('applicationStateManager').addToast('Advance accepted');
              this.get('router').transitionTo(EMBER_ROUTE_NAMES.PROJECT__DETAILS, this.get('state.application.id'));
            }).catch((error) => {
              handleErrorResponse(this, error, FORM_INPUTS);
            });
        } else {
          // Submitting a new advance
          this.get('stateManager.submitAdvancePayment').perform()
            .then(() => {
              this.set('errors', {});
              this.get('applicationStateManager').addToast('Advance submitted');
              this.get('router').transitionTo(EMBER_ROUTE_NAMES.PROJECT__DETAILS, this.get('state.application.id'));
            }).catch((error) => {
              handleErrorResponse(this, error, FORM_INPUTS);
              // handled by state manager
            });
        }
      } else {
        // Project completion workflow
        if (this.get('state.isBuyerViewing')) {
          // Buyer submit
          this.get('stateManager.updateProjectInformationFormBuyer').perform()
            .then(() => {
              this.set('errors', {});

              this.get('router').transitionTo(
                EMBER_ROUTE_NAMES.PROJECT__COMPLETE__PUBLIC_FEEDBACK,
                this.get('state.application.id'));
            }).catch((error) => {
              handleErrorResponse(this, error, FORM_INPUTS);
            });
        } else {
          // Seller submit
          this.get('stateManager.updateProjectInformationFormSeller').perform()
            .then(() => {
              this.set('errors', {});

              this.get('router').transitionTo(
                EMBER_ROUTE_NAMES.PROJECT__COMPLETE__PRIVATE_FEEDBACK,
                this.get('state.application.id'));
            }).catch((error) => {
              handleErrorResponse(this, error, FORM_INPUTS);
            });
        }
      }
    },

    onWithdrawExpeditedPayoutClicked() {
      this.setProperties({
        'state.expedite': false,
        'state.testBillingRecord.expedite': false,
      });

      this._updateTestBillingRecord.perform();
    },

    onTakeoverExpediteFeeClick(takeoverFee) {
      this.setProperties({
        'state.takeoverExpediteFee': takeoverFee,
        'state.expedite': true,
        'state.testBillingRecord.expedite': true,
      });

      this._updateTestBillingRecord.perform();
    },
  },
});
